import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { GlobeIcon, SunIcon, MoonIcon, ChevronUpIcon } from './common/Icons';
import LanguageCurrencyModal from './LanguageCurrencyModal';
import '../styles/TopNavbar.css';

/**
 * Concrete promises rather than slogans — these are the three things a buyer checks before paying a
 * stranger for a game account.
 *
 * Each splits into a `lead` that gets reversed out in a chip and a `rest` that trails it, so the row
 * carries emphasis instead of reading as one flat string.
 *
 * `secondary` marks the two that give way on narrow screens, where the row would otherwise crowd the
 * language and theme controls.
 */
const TRUST_NOTES = [
  { id: 'support', lead: '24/7', rest: 'human support' },
  { id: 'escrow', lead: 'Escrow-protected', rest: 'payments', secondary: true },
  { id: 'delivery', lead: 'Instant', rest: 'delivery', secondary: true },
];

const TopNavbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, currentLanguageData, supportedLanguages } = useLanguage();
  const { currentCurrency, changeCurrency, currentCurrencyData } = useCurrency();
  const [showModal, setShowModal] = useState(false);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
  };

  const handleCurrencyChange = (currCode) => {
    changeCurrency(currCode);
  };

  return (
    <>
      <div className="top-navbar">
        <div className="top-navbar-container">
          <div className="top-navbar-left">
            <ul className="top-navbar-notes">
              {TRUST_NOTES.map(({ id, lead, rest, secondary }) => (
                <li
                  key={id}
                  className={`top-navbar-note${secondary ? ' top-navbar-note--secondary' : ''}`}
                >
                  <span className="top-navbar-note-lead">{lead}</span>
                  <span className="top-navbar-note-rest">{rest}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="top-navbar-right">
            <div className="top-navbar-item">
              <button
                type="button"
                className="language-currency-btn"
                onClick={() => setShowModal(true)}
              >
                <GlobeIcon color="currentColor" size={12} />
                <span>
                  {currentLanguageData.nativeName} · {currentCurrencyData.code}
                </span>
                <ChevronUpIcon color="currentColor" size={8} />
              </button>
            </div>
            <div className="top-navbar-item">
              <button
                type="button"
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <MoonIcon color="currentColor" size={12} />
                ) : (
                  <SunIcon color="currentColor" size={12} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <LanguageCurrencyModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        currentLanguage={currentLanguage}
        currentCurrency={currentCurrency}
        onLanguageChange={handleLanguageChange}
        onCurrencyChange={handleCurrencyChange}
        supportedLanguages={supportedLanguages}
      />
    </>
  );
};

export default TopNavbar;
